import { useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import editorCopy from '@/i18n/ns/workspace-editors';
import { CanvasFooter, CanvasHeaderLeadTools } from '../canvas-footer';
import { useWorkspace } from '../context';
import { useIntlLocale } from '../intl-locale';
import { WorkspaceIcon } from '../icons';
import { createInputFromDraft, projectsApi } from './api';
import { projectScope, putProject } from './use-projects';
import { markProjectCreated, useCreatedProject, useProjectDrafts } from './draft-store';
import { endsLabel, firstName, itemKind, roleLabel } from './labels';
import { personInitials, ProjectMark } from './mark';
import { chatProject, rememberProjectPlace, rememberThreadPlace } from './place';
import type { ProjectDraft } from './types';

const key = (item: { kind: string; id: string }) => `${item.kind}:${item.id}`;

/**
 * A project drafted in chat (draft_project), beside the conversation. It is
 * changed by talking; nothing exists until Create. Create posts the latest draft.
 */
export function ProjectDraftCanvas({ threadId }: { threadId: string }) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const editor = useNamespaceCopy(editorCopy);
  const entries = useProjectDrafts(threadId);
  const createdHere = useCreatedProject(threadId);
  const placed = chatProject(ws);
  const owner = (ws.threadMeta?.access ?? 'owner') === 'owner';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const latest = entries?.at(-1);
  if (!latest)
    return (
      <div className="ws-canvas-body">
        <h1>{copy.aNewProject}</h1>
        <p className="ws-muted">{copy.draftIntro}</p>
      </div>
    );
  const { draft, version } = latest;
  // After a reload: the drafted chat already lives in a project, so this draft was created.
  const done =
    createdHere ?? (placed && draft.chatId && draft.chatId === ws.threadId ? placed : null);
  const previous = entries?.find((entry) => entry.version === version - 1)?.draft ?? null;
  const takenOut = previous
    ? previous.items.filter((item) => !draft.items.some((now) => key(now) === key(item)))
    : [];
  const added = new Set(
    previous
      ? draft.people
          .filter((person) => !previous.people.some((before) => before.userId === person.userId))
          .map((person) => person.userId ?? '')
      : [],
  );
  const create = async () => {
    setBusy(true);
    setError('');
    const inScope = projectScope();
    try {
      const project = await projectsApi.create(
        createInputFromDraft({ ...draft, chatId: draft.chatId ?? null }),
      );
      if (!inScope()) return;
      const place = { id: project.id, name: project.name, tint: project.tint };
      rememberProjectPlace(place);
      putProject(project);
      markProjectCreated(threadId, place);
      if (draft.chatId) rememberThreadPlace(draft.chatId, place);
      // The chat is done drafting: its next message is ordinary work in the project.
      if (ws.threadId === threadId && ws.turnContext.newProject) {
        const { newProject: _drafting, ...rest } = ws.turnContext;
        ws.setTurnContext(rest);
      }
      ws.refreshThreadMeta();
      ws.notify(formatCopy(copy.projectCreatedNotice, { name: project.name }), null, 'success');
      ws.openProject(project.id);
    } catch (failure) {
      setError(
        failure instanceof Error && failure.message ? failure.message : editor.couldntCreateProject,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <CanvasHeaderLeadTools>
        <span className="ws-project-where">
          {copy.draft} {version}
        </span>
      </CanvasHeaderLeadTools>
      <DraftBody draft={draft} takenOut={takenOut} added={added} version={version} />
      <CanvasFooter>
        <div className="ws-project-draft-foot">
          <small>
            <WorkspaceIcon name="check" size={13} />
            {error ? (
              <span role="alert">{error}</span>
            ) : done ? (
              copy.projectCreatedAccess
            ) : owner ? (
              copy.changeInChatDraft
            ) : (
              copy.chatOwnerCreate
            )}
          </small>
          {done ? (
            <button
              type="button"
              className="ws-button primary"
              onClick={() => ws.openProject(done.id)}
            >
              {copy.open} {done.name}
            </button>
          ) : (
            <button
              type="button"
              className="ws-button primary"
              disabled={busy || !owner}
              onClick={() => void create()}
            >
              {busy ? copy.creating : copy.createProject}
            </button>
          )}
        </div>
      </CanvasFooter>
    </>
  );
}

function DraftBody({
  draft,
  takenOut,
  added,
  version,
}: {
  draft: ProjectDraft;
  takenOut: ProjectDraft['items'];
  added: Set<string>;
  version: number;
}) {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const editor = useNamespaceCopy(editorCopy);
  const gaps = draft.readsFirst.filter((item) => item.cannotOpen && item.cannotOpen.length > 0);
  return (
    <div className="ws-project-draft">
      <ProjectMark name={draft.name} tint={draft.tint ?? 'sand'} size="lg" />
      <h1>{draft.name}</h1>
      {draft.brief && <p className="ws-project-draft-brief">{draft.brief}</p>}
      {draft.endsOn && (
        <div className="ws-eyebrow ws-project-eyebrow" style={{ marginTop: 10 }}>
          <WorkspaceIcon name="clock" size={13} />
          {endsLabel(draft.endsOn, false, locale)}
        </div>
      )}
      {draft.follows.length > 0 && (
        <section>
          <h2>{copy.everyChatFollows}</h2>
          <ul>
            {draft.follows.map((skill) => (
              <li key={skill.id}>
                <WorkspaceIcon name="skills" size={15} />
                <span>{skill.name}</span>
                <small>{copy.skill}</small>
              </li>
            ))}
          </ul>
        </section>
      )}
      {draft.readsFirst.length > 0 && (
        <section>
          <h2>{copy.andReadsFirst}</h2>
          <ul>
            {draft.readsFirst.map((item) => (
              <li key={key(item)}>
                <WorkspaceIcon name={item.kind === 'file' ? 'document' : 'knowledge'} size={15} />
                <span>{item.title}</span>
                <small>{item.kind === 'file' ? copy.file : copy.knowledge}</small>
              </li>
            ))}
          </ul>
          {gaps.map((item) => (
            <div className="ws-project-check" key={`gap:${key(item)}`} role="status">
              <WorkspaceIcon name="alert" size={15} />
              <div>
                {formatCopy(copy.cannotOpenDraft, {
                  people: item.cannotOpen!.map((person) => firstName(person.name)).join(', '),
                  title: item.title,
                })}
              </div>
            </div>
          ))}
        </section>
      )}
      <section>
        <h2>{formatCopy(copy.bringsIn, { count: draft.items.length })}</h2>
        {draft.items.length ? (
          <ul>
            {draft.items.map((item) => {
              const kind = itemKind(item, locale);
              const own = !item.owner || item.owner.userId === ws.user?.id;
              return (
                <li key={key(item)}>
                  <WorkspaceIcon name={kind.icon} size={15} />
                  <span>
                    {item.kind === 'chat' && item.id === ws.threadId ? copy.thisChat : item.title}
                  </span>
                  <small>
                    {own ? kind.label : `${kind.label} · ${firstName(item.owner!.name)}`}
                  </small>
                </li>
              );
            })}
            {takenOut.map((item) => (
              <li key={`out:${key(item)}`} className="ws-project-draft-out">
                <WorkspaceIcon name={itemKind(item, locale).icon} size={15} />
                <span>{item.title}</span>
                <small>{formatCopy(copy.takenOutDraft, { count: version })}</small>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ws-project-empty" style={{ margin: 0 }}>
            {copy.nothingYet}
          </p>
        )}
        {draft.dropped && draft.dropped.length > 0 && (
          <p className="ws-project-empty" style={{ margin: '8px 0 0' }}>
            {formatCopy(copy.leftOut, {
              items: draft.dropped.map((item) => item.title).join(', '),
            })}
          </p>
        )}
      </section>
      <section>
        <h2>{copy.whoCanUse}</h2>
        <div className="ws-project-people-chips">
          <span className="ws-project-person-chip">
            <span className="ws-project-face">
              {personInitials(ws.user?.displayName ?? copy.you)}
            </span>
            {copy.youLead}
          </span>
          {draft.people.map((person) => (
            <span
              key={person.userId ?? person.name}
              className={`ws-project-person-chip ${added.has(person.userId ?? '') ? 'added' : ''}`}
            >
              <span className="ws-project-face">{personInitials(person.name)}</span>
              {person.name}, {roleLabel(person.role, locale).toLowerCase()}
            </span>
          ))}
        </div>
        <div className="ws-project-note" style={{ marginTop: 14 }}>
          <WorkspaceIcon name="shield" size={15} />
          <span>
            {copy.beingInProject}
            {draft.people.length === 0 ? ` ${copy.onlyYouUntilShare}` : ''}
          </span>
        </div>
      </section>
    </div>
  );
}
